import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Pencil, Search, Send, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const SECTIONS = [
  ["photography", "Photography Sessions"],
  ["classes", "Classes & Workshops"],
  ["gift_cards", "Gift Cards"],
  ["inquiries", "Website Inquiries"],
];

const inputCls = "w-full border border-[#E5E5E2] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]";

const waLink = (phone, name) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.length === 10) d = "1" + d;
  if (!d) return null;
  return `https://wa.me/${d}?text=${encodeURIComponent(`Hi ${name || "there"}, this is Bahare — your photographer.`)}`;
};

export default function ClientsTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null);       // {email, name, phone}
  const [compose, setCompose] = useState(null); // {targets: [], all: bool, label: string}
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/clients").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-sm text-[#7A7A7A] py-10 text-center">Loading…</p>;

  const q = search.trim().toLowerCase();
  const match = (c) => !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || (c.phone || "").includes(q);

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/clients/${encodeURIComponent(edit.email)}`, {
        name: edit.name, phone: edit.phone,
        new_email: edit.new_email && edit.new_email !== edit.email ? edit.new_email : undefined,
      });
      toast.success("Client updated everywhere");
      setEdit(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/admin/clients/email", {
        to: compose.targets, all_clients: compose.all,
        subject: compose.subject, message: compose.message,
      });
      toast.success(`Email sent to ${r.data.sent} client${r.data.sent === 1 ? "" : "s"}${r.data.failed.length ? ` — failed: ${r.data.failed.join(", ")}` : ""}`);
      setCompose(null);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const totalClients = SECTIONS.reduce((n, [k]) => n + data[k].length, 0);

  return (
    <div data-testid="admin-clients" className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B5B5B0]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or phone…"
            className={`${inputCls} pl-9`} data-testid="clients-search" />
        </div>
        <button onClick={() => setCompose({ targets: [], all: true, label: `All clients (${totalClients})`, subject: "", message: "" })}
          data-testid="clients-email-all"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 py-3 hover:bg-[#4A4A4A]">
          <Mail size={13} /> Send Promotion to Everyone
        </button>
      </div>

      {SECTIONS.map(([key, label]) => {
        const list = data[key].filter(match);
        if (!list.length && q) return null;
        return (
          <div key={key} data-testid={`clients-section-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A]">{label} ({data[key].length})</p>
              {data[key].length > 0 && (
                <button onClick={() => setCompose({ targets: data[key].map((c) => c.email), all: false, label, subject: "", message: "" })}
                  data-testid={`clients-email-section-${key}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-3 py-1.5 hover:border-[#1A1A1A] transition-colors">
                  <Mail size={11} /> Email this section
                </button>
              )}
            </div>
            <div className="space-y-2">
              {list.map((c, i) => (
                <div key={c.id || i} className="bg-white border border-[#E5E5E2] px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2"
                  data-testid={`client-row-${key}-${i}`}>
                  <div className="min-w-[180px]">
                    <p className="text-sm font-medium">{c.name || "—"}</p>
                    <p className="text-xs text-[#7A7A7A]">{c.email}</p>
                  </div>
                  {c.phone && <p className="text-xs text-[#4A4A4A]">{c.phone}</p>}
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[#B5B5B0] flex-1">{c.detail}</p>
                  <div className="flex gap-2 ml-auto">
                    {waLink(c.phone, c.name) && (
                      <a href={waLink(c.phone, c.name)} target="_blank" rel="noopener noreferrer" data-testid={`client-whatsapp-${key}-${i}`}
                        title="Open WhatsApp chat"
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-3 py-2 hover:border-green-600 hover:text-green-700 transition-colors">
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                    )}
                    <button onClick={() => setCompose({ targets: [c.email], all: false, label: c.name || c.email, subject: "", message: "" })}
                      data-testid={`client-email-${key}-${i}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-3 py-2 hover:border-[#1A1A1A] transition-colors">
                      <Mail size={12} /> Email
                    </button>
                    <button onClick={() => setEdit({ email: c.email, name: c.name || "", phone: c.phone || "", new_email: c.email })}
                      data-testid={`client-edit-${key}-${i}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-3 py-2 hover:border-[#1A1A1A] transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                  </div>
                </div>
              ))}
              {!list.length && !q && <p className="text-xs text-[#B5B5B0] py-3">No one here yet.</p>}
            </div>
          </div>
        );
      })}

      {/* Edit panel */}
      {edit && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="client-edit-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setEdit(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif text-2xl">Edit Client</h3>
              <button onClick={() => setEdit(null)} data-testid="client-edit-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-[#7A7A7A] mb-6">Updates apply everywhere — bookings, calendar sessions, classes and gift cards.</p>
            <form onSubmit={saveEdit} className="space-y-3" data-testid="client-edit-form">
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="client-edit-name" />
              <input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="Phone" className={inputCls} data-testid="client-edit-phone" />
              <input type="email" value={edit.new_email} onChange={(e) => setEdit({ ...edit, new_email: e.target.value })} placeholder="Email" className={inputCls} data-testid="client-edit-email" />
              <button type="submit" disabled={saving} data-testid="client-edit-save"
                className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] disabled:opacity-50">
                {saving ? "Saving…" : "Save Client"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Compose panel */}
      {compose && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="client-compose-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCompose(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif text-2xl">Send Email</h3>
              <button onClick={() => setCompose(null)} data-testid="compose-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-[#7A7A7A] mb-6">
              To: <span className="text-[#1A1A1A] font-medium">{compose.label}</span>
              {!compose.all && compose.targets.length > 1 && ` (${compose.targets.length} people)`}
            </p>
            <form onSubmit={sendEmail} className="space-y-3" data-testid="compose-form">
              <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} required
                placeholder="Subject * — e.g. Holiday Mini Sessions are open" className={inputCls} data-testid="compose-subject" />
              <textarea value={compose.message} onChange={(e) => setCompose({ ...compose, message: e.target.value })} required rows={8}
                placeholder="Message * — each line becomes a paragraph. Clients can simply reply to reach you." className={`${inputCls} resize-y`} data-testid="compose-message" />
              <button type="submit" disabled={saving} data-testid="compose-send"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] disabled:opacity-50">
                <Send size={13} /> {saving ? "Sending…" : "Send Email"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
